import {
  Component,
  EventEmitter,
  LOCALE_ID,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_SELECT_CONFIG, MatSelectChange } from '@angular/material/select';
import { combineLatest, map } from 'rxjs';
import { ShopSelectionStore } from '../../../../core/state/shop-selection.store';
import { ShopSelectionService } from '../../../../core/services/shop-selection/shop-selection.service';
import { CartService } from '../../../../core/services/cart/cart.service';
import { FormGroupTyped } from '../../../../core/types/interfaces';
import { Department } from '../../../models/department.model';
import { Resident } from '../../../models/resident.model';
import { departmentTotals, residentTotals } from '../../../utils/cart-totals';
import { WebHeaderDropdownComponent } from '../web-header-dropdown/web-header-dropdown.component';
import { ProfileAvatarComponent } from '../../profile-avatar/profile-avatar.component';
import { PartnerLogoComponent } from '../../partner-logo/partner-logo.component';
import { InactiveResidentAlertComponent } from '../../inactive-resident-alert/inactive-resident-alert.component';
import { NoConnextAccountIdErrorComponent } from '../../no-connext-account-id-error/no-connext-account-id-error.component';
import { HasNoValidPaymentMethodPipe } from '../../../pipes/has-no-valid-payment-method.pipe';

interface ResidentSelectForm {
  department: Department;
  resident: Resident;
}

interface SelectionTotals {
  departments: Record<number, number>;
  residents: Record<number, number>;
}

const NO_TOTALS: SelectionTotals = { departments: {}, residents: {} };

@Component({
  selector: 'app-resident-selector',
  templateUrl: './resident-selector.component.html',
  styleUrls: ['./resident-selector.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    WebHeaderDropdownComponent,
    ProfileAvatarComponent,
    PartnerLogoComponent,
    InactiveResidentAlertComponent,
    NoConnextAccountIdErrorComponent,
    HasNoValidPaymentMethodPipe,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'selectOverlayPanel' },
    },
  ],
})
export class ResidentSelectorComponent {
  private readonly store = inject(ShopSelectionStore);
  private readonly shopSelectionService = inject(ShopSelectionService);
  private readonly cartService = inject(CartService);

  @Output() backdropVisible = new EventEmitter<boolean>();

  @ViewChild('departmentSelectComponent')
  departmentSelectComp!: WebHeaderDropdownComponent;
  @ViewChild('residentSelectComponent')
  residentSelectComp!: WebHeaderDropdownComponent;

  readonly customer = toSignal(this.store.customer$, { initialValue: null });
  readonly department = toSignal(this.store.department$, {
    initialValue: null,
  });
  readonly resident = toSignal(this.store.resident$, { initialValue: null });
  readonly totals = toSignal(
    combineLatest([
      this.cartService.getAllCarts(),
      this.store.department$,
      this.store.customer$,
    ]).pipe(
      map(([carts, department, customer]) => ({
        departments: departmentTotals(carts, customer?.departments),
        residents: residentTotals(carts, department),
      }))
    ),
    { initialValue: NO_TOTALS }
  );

  readonly residentSelectForm = inject(FormBuilder).group({
    department: [null, Validators.required],
    resident: [{ value: null, disabled: true }, Validators.required],
  }) as FormGroupTyped<ResidentSelectForm>;

  isBackdropVisible = false;

  constructor() {
    const departmentControl = this.residentSelectForm.get('department');
    const residentControl = this.residentSelectForm.get('resident');

    departmentControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        if (value && value.peopleCount > 0) {
          residentControl.enable({ emitEvent: false });
        } else {
          residentControl.disable({ emitEvent: false });
        }
      });

    this.store.department$
      .pipe(takeUntilDestroyed())
      .subscribe(department =>
        departmentControl.setValue(
          this.customer()?.departments.find(
            d => d.departmentId === department?.departmentId
          ) ?? null
        )
      );

    this.store.resident$
      .pipe(takeUntilDestroyed())
      .subscribe(resident => residentControl.setValue(resident));
  }

  selectDepartment(event: MatSelectChange<Department>): void {
    this.shopSelectionService.selectDepartment(event.value.departmentId);
  }

  selectResident(event: MatSelectChange<Resident>): void {
    this.shopSelectionService.selectResident(event.value.id);
  }

  closeSelect(e: Event): void {
    this.backdropChanged(false);
    this.departmentSelectComp.closeSelect(e);
    this.residentSelectComp.closeSelect(e);
  }

  backdropChanged(isOpen: boolean): void {
    this.isBackdropVisible = isOpen;
    this.backdropVisible.emit(isOpen);
  }
}
